import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ShieldCheck, Truck, RotateCcw, MessageCircle, Phone } from "lucide-react";
import { buildWhatsAppOrderUrl, WHATSAPP_DISPLAY } from "@/lib/contact";

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-14">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="aspect-square bg-[#F4F1EA] rounded-3xl animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 bg-[#F4F1EA] rounded animate-pulse w-3/4" />
            <div className="h-6 bg-[#F4F1EA] rounded animate-pulse w-1/4" />
            <div className="h-24 bg-[#F4F1EA] rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 text-center" data-testid="product-not-found">
        <h1 className="font-heading font-bold text-3xl text-[#1C1C1C]">Produk tidak dijumpai</h1>
        <Link to="/katalog" className="inline-block mt-6 text-[#E07A5F] font-semibold">
          Kembali ke katalog
        </Link>
      </div>
    );
  }

  const waUrl = buildWhatsAppOrderUrl(product.name, product.image);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-14" data-testid="product-detail-page">
      <Link
        to="/katalog"
        className="inline-flex items-center gap-2 text-sm font-medium text-[#4A4A4A] hover:text-[#E07A5F]"
        data-testid="back-to-catalog"
      >
        <ArrowLeft className="w-4 h-4" /> Kembali ke katalog
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mt-8">
        <div className="relative">
          <div className="aspect-square bg-[#F4F1EA] rounded-[2rem] overflow-hidden">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {product.badge && (
            <span className="absolute top-6 left-6 px-4 py-1.5 rounded-full bg-[#1C1C1C] text-[#FAF9F6] text-[10px] font-bold tracking-[0.2em] uppercase">
              {product.badge}
            </span>
          )}
        </div>

        <div>
          <div className="label-eyebrow text-[#E07A5F]">{product.category.replace("-", " ")}</div>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-[#1C1C1C] mt-3 text-balance" data-testid="product-name">
            {product.name}
          </h1>

          <div className="flex items-baseline gap-3 mt-5">
            <span className="font-heading font-bold text-4xl text-[#E07A5F]" data-testid="product-price">
              RM {product.price.toLocaleString("ms-MY", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
            <span className="text-sm text-[#4A4A4A]">/ unit</span>
          </div>

          <div className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full bg-[#81B29A]/15 border border-[#81B29A]/30">
            <span className="w-2 h-2 rounded-full bg-[#81B29A] animate-pulse" />
            <span className="text-xs font-bold text-[#1C1C1C] tracking-wider uppercase">Tempahan Khas Tersedia</span>
          </div>

          <p className="text-base text-[#4A4A4A] mt-6 leading-relaxed">{product.description}</p>

          {/* Specs */}
          <div className="mt-8 bg-white rounded-2xl border border-[rgba(28,28,28,0.08)] divide-y divide-[rgba(28,28,28,0.06)]">
            {Object.entries(product.specs).map(([k, v]) => (
              <div key={k} className="flex justify-between py-3 px-5 text-sm">
                <span className="text-[#4A4A4A]">{k}</span>
                <span className="font-semibold text-[#1C1C1C] text-right max-w-[60%]">{v}</span>
              </div>
            ))}
          </div>

          {/* WhatsApp CTA */}
          <a
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="whatsapp-order-button"
            className="mt-8 w-full inline-flex items-center justify-center gap-3 px-6 py-4 rounded-xl bg-[#25D366] text-white hover:bg-[#1EBE57] transition-all duration-300 font-semibold text-base shadow-[0_10px_30px_rgba(37,211,102,0.25)] hover:shadow-[0_14px_40px_rgba(37,211,102,0.35)]"
          >
            <MessageCircle className="w-5 h-5" />
            Tempah via WhatsApp
          </a>
          <p className="text-xs text-center text-[#4A4A4A] mt-3 leading-relaxed">
            Klik untuk berhubung terus dengan kami di <span className="font-semibold text-[#1C1C1C]">{WHATSAPP_DISPLAY}</span><br />
            Dapatkan sebut harga & konsultasi saiz percuma.
          </p>

          {/* Trust badges */}
          <div className="grid grid-cols-3 gap-3 mt-8">
            {[
              { icon: ShieldCheck, label: "Jaminan 1 Tahun" },
              { icon: Truck, label: "Penghantaran Cepat" },
              { icon: RotateCcw, label: "Custom Size" },
            ].map((b, i) => (
              <div key={i} className="text-center bg-[#F4F1EA] rounded-xl py-4 px-3">
                <b.icon className="w-5 h-5 text-[#81B29A] mx-auto" />
                <p className="text-xs font-semibold text-[#1C1C1C] mt-2 leading-tight">{b.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
